/**
 * Nutritional Analysis download — the compliance-menu export, launched from
 * the Production Records page (the school-admin compliance surface).
 *
 * Picks up to three months and a format; the server breaks the document into
 * weekly report pages per USDA meal-pattern age group (K–8 / 9–12 / PK, K–12
 * at breakfast). Server route:
 * `GET /api/menus/nutritional-analysis.csv|.pdf` (same school-staff gate as
 * the calendar export).
 */

import { Button } from '@ordo/ui/button'
import { Checkbox } from '@ordo/ui/checkbox'
import { FormDialog } from '@ordo/ui/form-dialog'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { toast } from '@ordo/ui/sonner'
import { FileSpreadsheet } from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'

import { buildMonthOptions, downloadNutritionalAnalysis, type NutritionalAnalysisFormat } from '@/api/menu-calendar'

/**
 * The five compliance documents, in packet order (PROD-1300). Each is its own
 * download: a school hands an auditor the calendar without the nutrient
 * workbook behind it, or the carb sheet to a school nurse on its own.
 */
const COMPLIANCE_DOCUMENTS: ReadonlyArray<{
  value: NutritionalAnalysisFormat
  label: string
  description: string
}> = [
  {
    value: 'compliance-calendar-pdf',
    label: 'Compliance Calendar (PDF)',
    description: 'One page per month per age group: every item with its portion, crediting, allergens and diets.',
  },
  {
    value: 'analysis-pdf',
    label: 'Nutritional Analysis (PDF)',
    description: 'USDA weekly summary with Pass/Fail against the meal pattern, plus the per-day detail sheets.',
  },
  {
    value: 'analysis-csv',
    label: 'Nutritional Analysis (CSV)',
    description: 'The same menu and nutrient rows as a spreadsheet.',
  },
  {
    value: 'carb-counts-pdf',
    label: 'Carb Counts (PDF)',
    description: 'Two pages: carbohydrate totals by meal, then by individual item.',
  },
  {
    value: 'recipes-pdf',
    label: 'Recipes (PDF)',
    description:
      'Every recipe served, printed once per serving size this school serves it at and scaled to that portion, each followed by its crediting at that serving.',
  },
  {
    value: 'cn-labels-zip',
    label: 'CN Labels (ZIP)',
    description: 'Every CN label and product formulation statement attached to the month’s items.',
  },
]

export function NutritionalAnalysisButton({
  locationId,
  schoolName,
  campuses = [],
}: {
  locationId: string | null
  schoolName: string | null
  /**
   * Every campus the user oversees. With two or more, the dialog asks which
   * campus to document (the Production Records page has no campus tabs to
   * pick it from any more); `locationId` / `schoolName` are the default.
   */
  campuses?: ReadonlyArray<{ id: string; name: string }>
}): ReactElement | null {
  const [open, setOpen] = useState(false)
  const [campusId, setCampusId] = useState<string | null>(locationId)
  const pickCampus = campuses.length > 1
  const campus = pickCampus ? (campuses.find((c) => c.id === campusId) ?? null) : null
  const activeLocationId = campus?.id ?? locationId
  const activeSchoolName = campus?.name ?? schoolName
  const options = useMemo(() => buildMonthOptions(), [])
  const firstMonth = options[0]?.value
  const [selected, setSelected] = useState<Set<string>>(() => new Set(firstMonth ? [firstMonth] : []))
  const [format, setFormat] = useState<NutritionalAnalysisFormat>('compliance-calendar-pdf')
  if (!locationId) {
    return null
  }

  const toggle = (value: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(value)
      } else {
        next.delete(value)
      }
      return next
    })
  }
  // Chronological, like the calendar dialog — pages and filename follow it.
  const months = options.map((option) => option.value).filter((value) => selected.has(value))

  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)}>
        <FileSpreadsheet aria-hidden />
        Compliance Docs
      </Button>
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title="Download compliance docs"
        description={`Audit documents for ${activeSchoolName ?? 'this school'}: the Nutritional Analysis (weekly reports per meal-pattern age group with crediting, serving sizes, daily milk, allergens, and substitutions, plus a per-item nutrition table) or a ZIP of the attached CN/PFS label documents.`}
        submitLabel="Download"
        submitDisabled={months.length === 0 || !activeLocationId}
        onSubmit={async () => {
          await downloadNutritionalAnalysis({
            locationId: activeLocationId ?? locationId,
            schoolName: activeSchoolName ?? 'School',
            months,
            format,
          })
          toast.success(
            `${COMPLIANCE_DOCUMENTS.find((doc) => doc.value === format)?.label ?? 'Compliance document'} downloaded`,
          )
        }}
        onReset={() => {
          setSelected(new Set(firstMonth ? [firstMonth] : []))
          setFormat('compliance-calendar-pdf')
          setCampusId(locationId)
        }}>
        <div className="flex flex-col gap-2">
          {pickCampus ? (
            <RadioGroup
              className="flex flex-col gap-2 px-3 py-1"
              aria-label="Campus"
              value={activeLocationId ?? ''}
              onValueChange={setCampusId}>
              {campuses.map((option) => (
                <label key={option.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <RadioGroupItem value={option.id} />
                  <span className="font-medium">{option.name}</span>
                </label>
              ))}
            </RadioGroup>
          ) : null}
          {options.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-3 text-sm hover:bg-accent/50">
              <Checkbox
                checked={selected.has(option.value)}
                onCheckedChange={(checked) => toggle(option.value, checked === true)}
              />
              <span className="font-medium">{option.label}</span>
            </label>
          ))}
          <RadioGroup
            className="flex flex-col gap-2 px-3 py-1"
            value={format}
            onValueChange={(value) => setFormat(value as NutritionalAnalysisFormat)}>
            {COMPLIANCE_DOCUMENTS.map((doc) => (
              <label key={doc.value} className="flex cursor-pointer items-start gap-2 text-sm">
                <RadioGroupItem value={doc.value} className="mt-0.5" />
                <span>
                  <span className="font-medium">{doc.label}</span>
                  <span className="block text-xs text-muted-foreground">{doc.description}</span>
                </span>
              </label>
            ))}
          </RadioGroup>
        </div>
      </FormDialog>
    </>
  )
}
